import * as React from 'react'
import {
  FlatList,
  Keyboard,
  Pressable,
  StyleProp,
  Text,
  View,
} from 'react-native'
import { countryCodes, CountryItem } from '../typo/country-codes'
import { useTheme } from '../../hooks/theme.hook'

interface CountryListProps
  extends Omit<
    React.ComponentProps<typeof FlatList<CountryItem>>,
    'data' | 'renderItem'
  > {
  lang?: string
  searchValue?: string
  pickerButtonOnPress?: (item: CountryItem) => void
}

export const CountryList = ({
  lang = 'en',
  searchValue = '',
  pickerButtonOnPress,
  contentContainerStyle: restContentContainerStyle,
  ...rest
}: CountryListProps) => {
  const theme = useTheme()

  const resultCountries = React.useMemo(() => {
    const lowerSearchValue = searchValue.toLowerCase()

    return countryCodes.filter((country) => {
      if (
        country?.dial_code.includes(searchValue) ||
        // Allow always to search by English name
        country?.name['en'].toLowerCase().includes(lowerSearchValue) ||
        country?.name[lang].toLowerCase().includes(lowerSearchValue)
      ) {
        return country
      }
    })
  }, [searchValue, lang])

  const handlePress = React.useCallback(
    (item: CountryItem) => {
      Keyboard.dismiss()
      if (typeof pickerButtonOnPress === 'function') {
        pickerButtonOnPress(item)
      }
    },
    [pickerButtonOnPress]
  )

  const renderItem = React.useCallback(
    ({ item }: { item: CountryItem }) => {
      const itemName = item.name[lang]
      const dial_code = item.dial_code
      const checkName = itemName.length ? itemName : item.name['en']

      return (
        <Pressable
          onPress={() => handlePress(item)}
          style={({ pressed }) => ({
            flexDirection: 'row',
            alignItems: 'center',
            paddingVertical: theme.spacing(3),
            paddingHorizontal: theme.spacing(3),
            maxWidth: '100%',
            backgroundColor: pressed ? theme.colors.surfaceVariant : undefined,
          })}
          android_ripple={{
            color: theme.colors.surfaceVariant,
          }}
        >
          <View style={{ minWidth: 50 }}>
            <Text style={{ color: theme.colors.onSurface }}>{dial_code}</Text>
          </View>
          <Text
            style={{
              marginLeft: theme.spacing(2),
              color: theme.colors.onSurface,
              flex: 1,
            }}
            numberOfLines={1}
          >
            {checkName}
          </Text>
        </Pressable>
      )
    },
    [lang, theme, handlePress]
  )

  const ItemSeparator = React.useCallback(
    () => <View style={{ height: theme.spacing(2) }} />,
    [theme]
  )

  const contentContainerStyle = React.useMemo(
    () => [
      { paddingTop: theme.spacing(3), paddingBottom: theme.spacing(2) },
      restContentContainerStyle,
    ],
    [theme, restContentContainerStyle]
  )

  return (
    <FlatList
      showsVerticalScrollIndicator={false}
      data={resultCountries}
      keyExtractor={(item, index) => '' + item.code + index}
      initialNumToRender={10}
      maxToRenderPerBatch={10}
      keyboardShouldPersistTaps={'handled'}
      renderItem={renderItem}
      ItemSeparatorComponent={ItemSeparator}
      contentContainerStyle={contentContainerStyle}
      {...rest}
    />
  )
}
